import type { Store } from 'effector'
import type { ViewComponent } from '../components/index.h'
import type { AdjustArrayFunc, AdjustObjFunc } from './index.h'

import { combine } from 'effector'
import { isType } from 'variant'
import { model as i18n } from '~/core/i18n'
import { resolveAppearanceRowTitle } from '~/features/appearance'
import { ssePurchasesModel } from '~/features/payment/sse-purchases'
import { Failed } from '../components/index.h'
import { $structure } from '../components/model'
import { getFormatFunction } from './lib'

/*
 * This store holds formatted dynamic components,
 * from appearance api for current module.
 */
export const $viewComponents: Store<ViewComponent[]> = combine(
  $structure,
  i18n.$t,
  ssePurchasesModel.$adjust,
  (rows, { t }, adjustFactory) => {
    const result = [] as ViewComponent[]

    if (!rows) return result

    for (const [id, { data, hasMore, desc }] of rows) {
      const { style, title, description, localizedTitleKey, contentType } = desc

      const view: ViewComponent = {
        id,
        content: [],
        isMoreThanOnePage: Boolean(hasMore) && !style?.topContent,
        appearance: {
          ...desc,
          title: resolveAppearanceRowTitle(title, localizedTitleKey, t),
          description: description,
        },
        type: data.type,
      }

      const isNoPayload =
        !('payload' in data) || data.payload == null || isType(data, Failed)

      const payload = isNoPayload ? null : data.payload

      const isZeroLengthArray = Array.isArray(payload) && payload.length === 0

      if (isNoPayload || isZeroLengthArray) {
        result.push(view)
        continue // skip to next dynamic component
      }

      const adjust = getFormatFunction(contentType)

      if (Array.isArray(payload)) {
        const formattedContent: unknown[] = []

        for (const item of payload) {
          formattedContent.push(
            (adjust as AdjustArrayFunc)(contentType, item, adjustFactory, t)
          )
        }
        view.content = formattedContent
      }

      // payload is not iterable
      else {
        view.content = (adjust as AdjustObjFunc)(
          payload as object,
          adjustFactory
        )
      }

      result.push(view)
    }

    return result
  }
)
